import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient'; // Adjust path if needed
import './HomePage.css'; // Make sure this path is correct

const HomePage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [profileCount, setProfileCount] = useState(null);
  const [compareCount, setCompareCount] = useState(null);
  const heroRef = useRef(null); // Keep refs if needed for other logic, though not used in current observer setup
  const statsRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    // Check if user is logged in
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setIsLoggedIn(!!data.session);
    };

    // Get profile count from database
    const getStats = async () => {
      try {
        const { count, error } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        if (!error) {
          setProfileCount(count);
        } else {
          console.error("Error fetching profile count:", error.message);
        }

        // Get comparison count
        const { count: compCount, error: compError } = await supabase
          .from('shown_pairs')
          .select('*', { count: 'exact', head: true });

        if (!compError) {
          setCompareCount(compCount);
        } else {
          console.error("Error fetching comparison count:", compError.message);
        }
      } catch (err) {
         console.error("Database error:", err);
      }
    };

    checkAuth();
    getStats();

    // Set up intersection observers for animations
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          // Optional: unobserve after first animation
          // observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Query elements within the homepage component scope
    const elements = document.querySelectorAll('.homepage .animate-on-scroll');
    elements.forEach(el => observer.observe(el));

    // Cleanup function
    return () => {
      elements.forEach(el => {
          if (el) { // Check if element still exists before unobserving
            observer.unobserve(el);
          }
      });
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero-bg"></div>
        <div className="section-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Are You the <span className="gradient-text">College Essay Champion</span>?
            </h1>
            <p className="hero-subtitle">
              Submit your essays, compete anonymously in the Arena, climb the rankings, and get paid when high schoolers unlock your winning content.
            </p>
            <div className="cta-container">
              {isLoggedIn ? (
                <Link to="/submit" className="cta-button primary pulse">
                  Enter the Essay Arena
                </Link>
              ) : (
                <Link to="/login" className="cta-button primary pulse">
                  Join the Competition
                </Link>
              )}
              <Link to="/leaderboard" className="cta-button secondary">
                View Champions
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            {/* Visual elements like cards */}
            <div className="rank-card floating">
              <div className="rank-header">
                <span className="rank-title">Your Ranking</span>
                <span className="rank-badge">Top 15%</span>
              </div>
              <div className="rank-body">
                <div className="rank-item">
                  <span className="rank-label">Global:</span>
                  <span className="rank-value">254</span>
                </div>
                <div className="rank-item">
                  <span className="rank-label">Class of 2025:</span>
                  <span className="rank-value">87</span>
                </div>
                <div className="rank-item">
                  <span className="rank-label">Among MIT admits:</span>
                  <span className="rank-value">12</span>
                </div>
              </div>
            </div>
            <div className="profile-card floating delay-1">
              <div className="profile-avatar">
                <div className="avatar-placeholder"></div>
                <div className="profile-rating">1420</div>
              </div>
              <div className="profile-stat">
                <span className="stat-name">SAT</span>
                <span className="stat-value">1560</span>
              </div>
              <div className="profile-stat">
                <span className="stat-name">GPA</span>
                <span className="stat-value">3.95</span>
              </div>
              <div className="profile-details">
                <div className="badge stanford">Stanford</div>
                <div className="badge">Class of 2025</div>
              </div>
            </div>
            <div className="comparison-card floating delay-2">
              <div className="comparison-header">
                <span>Profile Comparison</span>
              </div>
              <div className="comparison-body">
                <div className="profile-circle left">A</div>
                <div className="comparison-vs">VS</div>
                <div className="profile-circle right">B</div>
              </div>
              <div className="comparison-footer">
                <div className="vote-button left">Vote</div>
                <div className="vote-button right">Vote</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section animate-on-scroll" ref={statsRef}>
        <div className="stats-container">
          <div className="stat-card">
            <span className="stat-number">{profileCount ?? '10,000+'}</span> {/* Use nullish coalescing */}
            <span className="stat-label">Student Profiles</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{compareCount ?? '250,000+'}</span> {/* Use nullish coalescing */}
            <span className="stat-label">Profile Comparisons</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">30+</span>
            <span className="stat-label">Top Universities</span>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works animate-on-scroll">
        <h2 className="section-title">How the Essay Arena Works</h2>
        <div className="steps-container">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3 className="step-title">Submit Your Best Essays</h3>
            <p className="step-description">
              Share your winning essays and application materials in a secure, anonymous profile.
            </p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3 className="step-title">Enter the Arena</h3>
            <p className="step-description">
              Compete head-to-head anonymously as peers vote on essay matchups to determine the strongest content.
            </p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3 className="step-title">Climb the Rankings</h3>
            <p className="step-description">
              See exactly where you stand among your class, specific majors, and successful admits to top programs while staying anonymous.
            </p>
          </div>
          <div className="step-card">
            <div className="step-number">4</div>
            <h3 className="step-title">Get Paid</h3>
            <p className="step-description">
              Add your Venmo and get paid when high schoolers pay to unlock the best essays from top-ranked writers.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section animate-on-scroll" ref={featuresRef}>
        <h2 className="section-title">Why Students Join the Arena</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon ranking-icon"></div>
            <h3 className="feature-title">Earn Money from Essays</h3>
            <p className="feature-description">
              Get paid when high schoolers unlock your top-ranked essays. Higher rankings mean more unlocks and more earnings.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon insights-icon"></div>
            <h3 className="feature-title">Beat Imposter Syndrome</h3>
            <p className="feature-description">
              Stop wondering if your essays are actually good. Get objective proof of where you stand among thousands of college applicants.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon anonymous-icon"></div>
            <h3 className="feature-title">Anonymous Competition</h3>
            <p className="feature-description">
              Compete fairly with essays judged on quality alone—no names, just pure content and writing excellence.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon compare-icon"></div>
            <h3 className="feature-title">Champion Recognition</h3>
            <p className="feature-description">
              Climb the leaderboards and earn bragging rights as one of the top essay writers in your class or major.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section animate-on-scroll">
        <h2 className="section-title">Champion Voices</h2>
        <div className="testimonials-container">
          <div className="testimonial-card">
            <div className="quote-mark">"</div>
            <p className="testimonial-text">
              I was shocked to see my Common App essay ranked in the top 5% of my class! The confidence boost was incredible, and I learned so much from reading other champion essays.
            </p>
            <div className="testimonial-author">
              <span className="author-name">Alex C.</span>
              <span className="author-details">Harvard '26</span>
            </div>
          </div>
          <div className="testimonial-card">
            <div className="quote-mark">"</div>
            <p className="testimonial-text">
              Being crowned the #3 essay writer among MIT admits validated all my hard work. Now I help other students improve their essays based on what I learned in the Arena.
            </p>
            <div className="testimonial-author">
              <span className="author-name">Priya K.</span>
              <span className="author-details">MIT '25</span>
            </div>
          </div>
          <div className="testimonial-card">
            <div className="quote-mark">"</div>
            <p className="testimonial-text">
              I was average at first, but after studying the top essays in my major, I completely transformed my writing style. Jumped from the bottom 50% to the top 20% in three weeks!
            </p>
            <div className="testimonial-author">
              <span className="author-name">Marcus T.</span>
              <span className="author-details">Stanford '27</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section animate-on-scroll">
        <div className="cta-content">
          <h2 className="cta-title">Ready to Claim Your Essay Champion Status?</h2>
          <p className="cta-description">
            Join thousands of students discovering where they truly stand. Enter the Arena today and see if your essays have what it takes to reach the top.
          </p>
          <div className="cta-buttons">
            {isLoggedIn ? (
              <Link to="/submit" className="cta-button primary large">
                Enter the Essay Arena Now
              </Link>
            ) : (
              <Link to="/login" className="cta-button primary large">
                Join the Competition Today
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;